import { useState, useEffect, useCallback, useRef } from "react";
import { usePublicClient } from "wagmi";
import notify from "../components/common/notify";

/**
 * Hook for monitoring blockchain transactions with retry logic and recovery
 * 
 * Features:
 * - Real-time transaction status monitoring
 * - Automatic retry on failure
 * - LocalStorage persistence for recovery on page refresh
 * - Configurable polling interval and timeout
 * 
 * @param {Object} options - Configuration options
 * @param {string} options.communityId - Community ID for transaction tracking
 * @param {Function} options.onSuccess - Callback when transaction succeeds
 * @param {Function} options.onError - Callback when transaction fails
 * @param {number} options.pollingInterval - Polling interval in milliseconds (default: 2000)
 * @param {number} options.timeout - Timeout in milliseconds (default: 300000 - 5 minutes)
 */
const useTransactionMonitor = ({
  communityId,
  onSuccess,
  onError,
  pollingInterval = 2000,
  timeout = 300000, // 5 minutes
} = {}) => {
  const [pendingTx, setPendingTx] = useState(null);
  const [txStatus, setTxStatus] = useState(null); // 'pending', 'mining', 'confirmed', 'failed'
  const [elapsedTime, setElapsedTime] = useState(0);
  const [retryCount, setRetryCount] = useState(0);

  const publicClient = usePublicClient();
  const pollingIntervalRef = useRef(null);
  const timeoutRef = useRef(null);
  const startTimeRef = useRef(null);
  // Track if onSuccess has already been called to prevent double-calling
  const successCalledRef = useRef(false);
  // Track if currently processing a confirmed transaction
  const processingRef = useRef(false);

  const STORAGE_KEY = `tx_monitor_${communityId || 'general'}`;

  /**
   * Save pending transaction to localStorage for recovery
   */
  const savePendingTx = useCallback((tx) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        ...tx,
        savedAt: new Date().toISOString(),
      }));
    } catch (err) {
      console.error("Failed to save pending transaction:", err);
    }
  }, [STORAGE_KEY]);

  /**
   * Load pending transaction from localStorage
   */
  const loadPendingTx = useCallback(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const tx = JSON.parse(stored);
        // Only restore transactions less than 1 hour old
        const savedAt = new Date(tx.savedAt);
        const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000);
        if (savedAt > oneHourAgo) {
          return tx;
        } else {
          // Clean up old transaction
          localStorage.removeItem(STORAGE_KEY);
        }
      }
    } catch (err) {
      console.error("Failed to load pending transaction:", err);
    }
    return null;
  }, [STORAGE_KEY]);

  /**
   * Clear pending transaction from localStorage
   */
  const clearPendingTx = useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (err) {
      console.error("Failed to clear pending transaction:", err);
    }
  }, [STORAGE_KEY]);

  /**
   * Check transaction status on blockchain
   */
  const checkTransactionStatus = useCallback(async (txHash) => {
    if (!publicClient || !txHash) return null;

    try {
      // Get transaction receipt - this throws TransactionReceiptNotFoundError if not yet mined
      const receipt = await publicClient.getTransactionReceipt({ hash: txHash });
      
      if (receipt) {
        // Transaction is mined
        return {
          status: receipt.status === 'success' ? 'confirmed' : 'failed',
          blockNumber: receipt.blockNumber,
          gasUsed: receipt.gasUsed?.toString(),
          receipt,
        };
      } else {
        // Receipt returned but null - still pending
        return { status: 'mining' };
      }
    } catch (err) {
      const errorMessage = err?.message || String(err);
      const errorName = err?.name || '';
      const errorString = String(err);
      
      // Handle "TransactionReceiptNotFoundError" - transaction is pending, not yet mined
      if (errorName.includes('TransactionReceiptNotFound') || 
          errorMessage.includes('could not be found') ||
          errorMessage.includes('not be processed on a block yet')) {
        // Transaction exists but not yet mined - this is normal, keep polling (silent)
        return { status: 'mining' };
      }
      
      // Handle timeout and other RPC errors gracefully - continue polling
      // Check error name, message, and string representation for timeout indicators
      const isTimeout = 
        errorName === 'TimeoutError' ||
        errorName.includes('Timeout') ||
        errorMessage.includes('timeout') || 
        errorMessage.includes('TimeoutError') || 
        errorMessage.includes('took too long') || 
        errorMessage.includes('ETIMEDOUT') ||
        errorString.includes('TimeoutError');
      
      if (isTimeout) {
        // Silent retry on timeout - don't flood console
        return { status: 'mining' };
      }
      
      // Handle rate limiting and network errors - also keep polling silently
      if (errorMessage.includes('429') || 
          errorMessage.includes('rate limit') ||
          errorMessage.includes('Too Many Requests') ||
          errorMessage.includes('fetch failed') ||
          errorMessage.includes('network') ||
          errorName.includes('FetchError')) {
        return { status: 'mining' };
      }
      
      // Only log truly unexpected errors (once)
      console.error("Error checking transaction status:", err);
      return { status: 'error', error: err.message };
    }
  }, [publicClient]);

  /**
   * Start monitoring a transaction
   */
  const monitorTransaction = useCallback((txHash, metadata = {}) => {
    if (!txHash) return;

    // Reset success flag when starting a new monitor
    successCalledRef.current = false;
    processingRef.current = false;

    const tx = {
      hash: txHash,
      startedAt: new Date().toISOString(),
      metadata,
    };

    setPendingTx(tx);
    setTxStatus('pending');
    setElapsedTime(0);
    setRetryCount(0);
    startTimeRef.current = Date.now();

    savePendingTx(tx);

    // Start polling
    pollingIntervalRef.current = setInterval(async () => {
      // Skip if already processing or success was already called
      if (processingRef.current || successCalledRef.current) return;
      
      const result = await checkTransactionStatus(txHash);
      
      if (result) {
        if (result.status === 'confirmed') {
          // Prevent double processing
          if (successCalledRef.current || processingRef.current) return;
          processingRef.current = true;
          
          setTxStatus('confirmed');
          clearInterval(pollingIntervalRef.current);
          clearTimeout(timeoutRef.current);
          // Clear from localStorage FIRST before calling onSuccess
          clearPendingTx();
          
          if (onSuccess && !successCalledRef.current) {
            successCalledRef.current = true;
            onSuccess({ ...result, metadata });
          }
          notify("success", "Transaction confirmed!");
        } else if (result.status === 'failed' || result.status === 'error') {
          setTxStatus('failed');
          clearInterval(pollingIntervalRef.current);
          clearTimeout(timeoutRef.current);
          clearPendingTx();
          
          if (onError) {
            onError(result);
          }
          notify("error", "Transaction failed");
        } else if (result.status === 'mining') {
          setTxStatus('mining');
        } else if (result.status === 'not_found') {
          setTxStatus('failed');
          clearInterval(pollingIntervalRef.current);
          clearTimeout(timeoutRef.current);
          clearPendingTx();
          
          if (onError) {
            onError({ error: 'Transaction not found' });
          }
          notify("error", "Transaction not found - may have been dropped");
        }
      }

      // Update elapsed time
      setElapsedTime(Math.floor((Date.now() - startTimeRef.current) / 1000));
    }, pollingInterval);

    // Set timeout
    timeoutRef.current = setTimeout(() => {
      clearInterval(pollingIntervalRef.current);
      setTxStatus('timeout');
      notify("warning", "Transaction taking longer than expected. Check your wallet or block explorer.");
    }, timeout);

  }, [checkTransactionStatus, onSuccess, onError, pollingInterval, timeout, savePendingTx, clearPendingTx]);

  /**
   * Retry checking a pending transaction
   */
  const retryTransaction = useCallback(async () => {
    if (!pendingTx || !pendingTx.hash) return;
    // Don't retry if already succeeded
    if (successCalledRef.current) return;

    setRetryCount(prev => prev + 1);
    const result = await checkTransactionStatus(pendingTx.hash);
    
    if (result && result.status === 'confirmed') {
      // Prevent double processing
      if (successCalledRef.current || processingRef.current) return;
      processingRef.current = true;
      
      clearPendingTx();
      setPendingTx(null);
      setTxStatus('confirmed');
      
      if (onSuccess && !successCalledRef.current) {
        successCalledRef.current = true;
        onSuccess({ ...result, metadata: pendingTx.metadata });
      }
    } else if (result && (result.status === 'failed' || result.status === 'error')) {
      if (onError) {
        onError(result);
      }
      setTxStatus('failed');
    } else {
      // Restart monitoring
      monitorTransaction(pendingTx.hash, pendingTx.metadata);
    }
  }, [pendingTx, checkTransactionStatus, onSuccess, onError, clearPendingTx, monitorTransaction]);

  /**
   * Cancel monitoring
   */
  const cancelMonitoring = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    clearPendingTx();
    setPendingTx(null);
    setTxStatus(null);
    setElapsedTime(0);
    // Reset refs
    successCalledRef.current = false;
    processingRef.current = false;
  }, [clearPendingTx]);

  /**
   * On component mount, check for pending transactions
   */
  useEffect(() => {
    // Skip if success was already called (e.g., from polling)
    if (successCalledRef.current) return;
    
    const savedTx = loadPendingTx();
    if (savedTx && savedTx.hash) {
      // Resume monitoring
      setPendingTx(savedTx);
      setTxStatus('pending');
      
      // Check current status
      checkTransactionStatus(savedTx.hash).then((result) => {
        // Double-check that success wasn't called while we were checking
        if (successCalledRef.current) return;
        
        if (result) {
          if (result.status === 'confirmed') {
            // Prevent double processing
            if (successCalledRef.current || processingRef.current) return;
            processingRef.current = true;
            
            setTxStatus('confirmed');
            // Clear from localStorage FIRST
            clearPendingTx();
            
            if (onSuccess && !successCalledRef.current) {
              successCalledRef.current = true;
              onSuccess({ ...result, metadata: savedTx.metadata });
            }
            notify("info", "Previous transaction was confirmed");
          } else if (result.status === 'failed' || result.status === 'error') {
            setTxStatus('failed');
            clearPendingTx();
            notify("warning", "Previous transaction failed");
          } else {
            // Resume monitoring
            monitorTransaction(savedTx.hash, savedTx.metadata);
          }
        }
      });
    }

    // Cleanup on unmount
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [loadPendingTx, checkTransactionStatus, clearPendingTx, onSuccess, monitorTransaction]);

  return {
    // State
    pendingTx,
    txStatus,
    elapsedTime,
    retryCount,
    isMonitoring: !!pendingTx && (txStatus === 'pending' || txStatus === 'mining'),
    
    // Actions
    monitorTransaction,
    retryTransaction,
    cancelMonitoring,
  };
};

export default useTransactionMonitor;
